<template>
  <div class="container-fluid" id="assumptions">
    <div class="row wrapper">
      <div class="col-12 col-md-4">
        <div class="header">
          <div id="steps" class="steps" tabindex="0" role="document">
            &nbsp;
          </div>
          <h1 tabindex="0">Edit assumptions</h1>
				<div class="hero-image" alt="Edit assumptions"></div>
        </div>
      </div>
      <div class="col-12 col-md-8">
        <div class="main assumptions-field">

			<b-tabs content-class="mt-3" justified>
				<b-tab title="Disclaimer" :active="activeTab === 'disclaimer'">
					<section class="disclaimer-content">
            <div v-html="pageContent.pages.assumptions.content"></div>
          </section>
				</b-tab>
				<b-tab title="Edit assumptions" :active="activeTab === 'assumptions'">

					<p>
					The following assumptions have been made when calculating your estimated insurance needs. You can change the values of these assumptions below.
					</p>

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								fieldName="wageInflation"
								storeType="editAssumptions"
								fieldLabel="Wage inflation"
                customClass="percentage"
								tooltip="Wage inflation is used to calculate your future level of income so that the calculator can determine how much TPD insurance is needed. TPD insurance will be used to supplement your future income needs."
								:customBlurFunction="triggerHeightCheck"
                :fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.inflation.min"
								:fieldMax="config.fields.inflation.max"
								:errorMessage="pageContent.pages.assumptions.wageInflationError">
							</CustomField>
						</div>
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								fieldName="priceInflation"
								storeType="editAssumptions"
								fieldLabel="Price inflation"
                customClass="percentage"
								tooltip="The calculator considers price inflation when determining how much you might need to pay expenses in the future."
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.inflation.min"
								:fieldMax="config.fields.inflation.max"
								:errorMessage="pageContent.pages.assumptions.priceInflationError">
							</CustomField>
						</div>
					</div>

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								fieldName="retirementAge"
								storeType="editAssumptions"
								fieldLabel="Retirement age"
								:tooltip="`Should the unexpected happen, the calculator assumes that income and living expenses for you and your beneficiaries will need to be provided for through insurance until your retirement age.`"
								:fieldOptions="formatOptions.age"
								:fieldMin="config.fields.retirementAge.min"
								:fieldMax="config.fields.retirementAge.max"
								:customBlurFunction="updateRetirementAgeRelatedFieldValues"
								:errorMessage="pageContent.pages.assumptions.retirementAgeError">
							</CustomField>
						</div>
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								fieldName="lifeInsuranceInvestmentReturn"
								storeType="editAssumptions"
								fieldLabel="Discount rate"
                customClass="percentage"
								tooltip="The calculator allows for cashflows such as expenses which will be incurred in the future. The calculator brings those future cashflows to present values using this 'discount rate'."
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.investmentReturn.min"
								:fieldMax="config.fields.investmentReturn.max"
								:errorMessage="pageContent.pages.assumptions.investmentReturnError">
							</CustomField>
						</div>
					</div>

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<label for="" tabindex="0">TPD to include debt &amp; liabilities
                <b-button id="tooltip-assumptions-tpd-debt" variant="tooltip" aria-label="tooltip tpd include debt and liabilities"><i class="icon-help"></i></b-button>
                <b-tooltip target="tooltip-assumptions-tpd-debt" placement="top">
                  If you select yes, the calculator will include your debt and liability to your TPD cover on top of your ongoing expenses so that you may be able to pay them in full.
                </b-tooltip>
              </label>
							<select class="form-control" aria-label="Default select example" v-model="data.editAssumptions.tpdRepayDebtsLiabilities">
								<option selected disabled value="">Select option</option>
								<option :value="true">Yes</option>
								<option :value="false">No</option>
							</select>
						</div>
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								fieldName="tpdFutureIncomeLevel"
								storeType="editAssumptions"
								fieldLabel="TPD income replacement %"
                customClass="percentage"
								tooltip="This is the percentage of your future income that is replaced through a combination of TPD cover and income protection in total. Part of your income level may be covered by income protection and TPD will cover the remaining amount. The default of 100% is based on the assumption that your current income would be needed to continue to pay for your living expenses and any debts."
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.tpdFutureIncomeLevel.min"
								:fieldMax="config.fields.tpdFutureIncomeLevel.max"
								:errorMessage="pageContent.pages.assumptions.tpdFutureIncomeError">
							</CustomField>
						</div>
					</div>

					<!-- <div class="row">
						<div class="col">
							<label for="">TPD age to cease allowance for future income</label>
							<b-button id="tooltip-assumptions-9" variant="tooltip"><i class="icon-help"></i></b-button>
							<b-tooltip target="tooltip-assumptions-9" placement="top">
								Hello <strong>World!</strong>
							</b-tooltip>
							<br>
							<input class="form-control" name='tpdCeaseAllowanceForFutureIncomeAtAge' id='tpdCeaseAllowanceForFutureIncomeAtAge' v-model.number="tpdCeaseAllowanceForFutureIncomeAtAge" />
						</div>
					</div> -->

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								fieldName="incomeProtectionSuperGuarantee"
								storeType="editAssumptions"
								fieldLabel="Super contribution rate"
                customClass="percentage"
								tooltip="Super guarantee (SG) is the minimum percentage of your earnings that your employer is required to contribute into your super. The SG rate is set by the Australian Government at 12% from 1 July 2025 with a maximum based on the maximum contributions base. However, you may be making more contributions. You can change the super contribution rate we take into account. See Assumptions & Disclaimer for further details."
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.sgRate.min"
								:fieldMax="config.fields.sgRate.max"
								:errorMessage="pageContent.pages.assumptions.sgContributionError">
							</CustomField>
						</div>
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								fieldName="incomeProtectionPercentage"
								storeType="editAssumptions"
								fieldLabel="Income protection %"
                customClass="percentage"
								:tooltip="pageContent.pages.assumptions.ipPercentageTooltip"
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.incomeProtectionPercentage.min"
								:fieldMax="config.fields.incomeProtectionPercentage.max"
								:errorMessage="pageContent.pages.assumptions.ipPercentageError">
							</CustomField>
						</div>
					</div>

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
                :readonly="true"
								fieldName="lifeInsurancePaymentPeriod"
								storeType="editAssumptions"
								fieldLabel="Payment period of rent and expenses covered until age"
								:tooltip="pageContent.pages.assumptions.rentExpensesTooltip"
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.years"
								:fieldMin="config.fields.lifeInsurancePaymentPeriod.min"
								:fieldMax="config.fields.lifeInsurancePaymentPeriod.max">
							</CustomField>
						</div>
					</div>
					<button class="reset-assumptions-button" v-on:click="resetAssumptions"><i class="icon-undo" tabindex="0"></i> Reset assumptions</button>

				</b-tab>

			</b-tabs>

			<div class="nav-button row">
				<button class="back-button col-auto" @click="$router.go(-1)" tabindex="0" role="button">Back</button>
			</div>

        </div>
      </div>

    </div>
		<CustomFooter></CustomFooter>
  </div>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapGetters } from 'vuex';
import CustomField from '../components/CustomField';
import CustomFooter from '../components/CustomFooter';

export default {
  name: 'Assumptions',
  props: ['activeTab'],
  components: { CustomField, CustomFooter },
	computed: {
    ...mapFields(["data"]),
		...mapGetters(["config", "formatOptions", "pageContent"]),
  },
	methods: {
		resetAssumptions: function() {
			// console.log('reset to defaults');
			this.$store.commit("setDefaultAssumptions");
		},
		updateRetirementAgeRelatedFieldValues: function() {
			this.$store.commit("setLifeInsurancePaymentPeriod");
			this.$store.commit("settpdCeaseAllowanceForFutureIncomeAtAge");
      this.triggerHeightCheck();
		},
    triggerHeightCheck: function() {
      // Add slight delay otherwise incorrect height is read
      var rf = this;
      setTimeout(function() {
        rf.$refreshIframeHeight();
      }, 300);
    }
	},
	mounted() {
		this.$store.commit("setLifeInsurancePaymentPeriod");
		this.$store.commit("settpdCeaseAllowanceForFutureIncomeAtAge");
		this.triggerHeightCheck();
    this.$scrollIframeTop();
    window.addEventListener("resize", this.$refreshIframeHeight);
	},
  destroyed() {
    window.removeEventListener("resize", this.$refreshIframeHeight);
  },
	beforeDestroy() {
		// console.log(JSON.stringify(this.data.editAssumptions));
    // send analytics - update assumptions\
    this.$gtag.event("assumptions", {
      "event_category": "update-assumptions",
      "event_label": JSON.stringify(this.data.editAssumptions),
    })
  },
}
</script>
